import { SystemStyleObject } from '@chakra-ui/react';
import {
  chakraComponents,
  GroupBase,
  Select as ChakraSelect,
  SelectComponentsConfig,
} from 'chakra-react-select';
import { ReactNode } from 'react';

import { pxToRem, TRANSITIONS } from '../theme';
import { ChevronDownIcon } from './icons';

export type SelectStylesProp = {
  container?: SystemStyleObject;
  menuList?: SystemStyleObject;
  option?: SystemStyleObject;
  control?: SystemStyleObject;
  singleValue?: SystemStyleObject;
  valueContainer?: SystemStyleObject;
  dropdownIndicator?: SystemStyleObject;
};

type OptionType<T> = { value: T; label: ReactNode };

export type SelectProps<T> = {
  value: T;
  setValue: (value: T) => void;
  options: OptionType<T>[];
  styles?: SelectStylesProp;
  placeholder?: string;
  required?: boolean;
  name?: string;
  isSearchable?: boolean;
  components?: SelectComponentsConfig<OptionType<T>, false, GroupBase<OptionType<T>>>;
};

export function Select<T>({
  value,
  setValue,
  options,
  styles,
  components,
  ...rest
}: SelectProps<T>) {
  return (
    <ChakraSelect
      isSearchable={false}
      value={options.find((option) => option.value === value)}
      onChange={(option) => (option ? setValue(option?.value) : undefined)}
      options={options}
      chakraStyles={{
        container: (provided) => ({
          ...provided,
          cursor: 'pointer',
          w: pxToRem(184),
          ...styles?.container,
        }),
        valueContainer: (provided) => ({
          ...provided,
          ...styles?.singleValue,
        }),
        control: (provided) => ({
          ...provided,
          fontSize: 'sm',
          minHeight: pxToRem(40),
          h: 'auto',
          px: 3,
          ...styles?.control,
        }),
        menuList: (provided) => ({
          ...provided,
          py: 1,
          border: 'none',
          bg: 'backgroundTertiary',
          ...styles?.menuList,
        }),
        option: () => ({
          px: 3,
          py: 2,
          transform: TRANSITIONS.standard,
          fontSize: 'sm',
          '&:hover': {
            bg: 'backgroundSecondary',
          },
          ...styles?.option,
        }),
        dropdownIndicator: (provided) => ({
          ...provided,
          ...styles?.dropdownIndicator,
        }),
      }}
      components={{
        DropdownIndicator: (props) => (
          <chakraComponents.DropdownIndicator {...props}>
            <ChevronDownIcon
              boxSize="3"
              transition={TRANSITIONS.standard}
              transform={`rotateZ(${props.selectProps.menuIsOpen ? 180 : 0}deg)`}
            />
          </chakraComponents.DropdownIndicator>
        ),
        ...components,
      }}
      {...rest}
    />
  );
}
